import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import {
  Checkbox,
  Combobox,
  DataTable,
  DataTableCell,
  DataTableColumn
} from '@salesforce/design-system-react';

import './LocationAttributeMapping.scss';

const CustomDataTableCell = ({ children, onClick, property, ...props }) => {
  if (!children) return null;

  const selection = children.value
    ? [
        {
          id: children.value,
          label: children.value,
          value: children.value,
          mapTo: children.mapTo
        }
      ]
    : [];
  return (
    <td className="location-attribute-cell">
      <Combobox
        options={attributeOptions(props.attributes, props.mapping, children)}
        selection={selection}
        variant="readonly"
        events={{
          onSelect: (evt, data) => {
            const change = {};
            change[children.mapTo] = data.selection[0]?.value;
            props.onSelect(change);
          }
        }}
      />
    </td>
  );
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  item: PropTypes.object
};

function attributeOptions(attributes, mapping, currentSelection) {
  // Options here consist of the attributes selected in the previous step,
  // minus the attributes which are already mapped to other location fields.
  const inUse = Object.values(mapping).filter(
    attrName => attrName !== currentSelection.value
  );
  const options = attributes.filter(name => !inUse.includes(name));
  return options.map(opt => ({
    id: opt,
    label: opt,
    value: opt,
    mapTo: currentSelection.mapTo
  }));
}

class LocationAttributeMapping extends Component {
  constructor(props) {
    super(props);
    const mapping = props.mapping || {};
    this.state = {
      showLanguage: !!mapping.language,
      showPostal: !!mapping.postal_code,
      showCity: !!mapping.city || !!mapping.state || !!mapping.country,
      showLatLong: !!mapping.latitude || !!mapping.longitude
    };
  }

  renderMappingTable() {
    const { formatMessage } = this.props.intl;
    const items = [];
    const mapping = this.props.mapping;
    if (this.state.showLanguage) {
      items.push({
        id: 'language',
        location_attribute: formatMessage({
          id: 'components.ecs.profiles.locationMapping.language'
        }),
        profile_attribute: {
          mapTo: 'language',
          value: mapping?.language
        }
      });
    }
    if (this.state.showPostal) {
      items.push({
        id: 'postal_code',
        location_attribute: formatMessage({
          id: 'components.ecs.profiles.locationMapping.postalCode'
        }),
        profile_attribute: {
          mapTo: 'postal_code',
          value: mapping?.postal_code
        }
      });
    }
    if (this.state.showCity) {
      items.push(
        {
          id: 'city',
          location_attribute: formatMessage({
            id: 'components.ecs.profiles.locationMapping.city'
          }),
          profile_attribute: {
            mapTo: 'city',
            value: mapping?.city
          }
        },
        {
          id: 'state',
          location_attribute: formatMessage({
            id: 'components.ecs.profiles.locationMapping.state'
          }),
          profile_attribute: {
            mapTo: 'state',
            value: mapping?.state
          }
        }
      );
    }
    // Country is required for postal and for city, but don't show it twice
    if (this.state.showPostal || this.state.showCity) {
      items.push({
        id: 'country',
        location_attribute: formatMessage({
          id: 'components.ecs.profiles.locationMapping.country'
        }),
        profile_attribute: {
          mapTo: 'country',
          value: mapping?.country
        }
      });
    }
    if (this.state.showLatLong) {
      items.push(
        {
          id: 'latitude',
          location_attribute: formatMessage({
            id: 'components.ecs.profiles.locationMapping.latitude'
          }),
          profile_attribute: {
            mapTo: 'latitude',
            value: mapping?.latitude
          }
        },
        {
          id: 'longitude',
          location_attribute: formatMessage({
            id: 'components.ecs.profiles.locationMapping.longitude'
          }),
          profile_attribute: {
            mapTo: 'longitude',
            value: mapping?.longitude
          }
        }
      );
    }

    return (
      <DataTable className="location-mapping-table" items={items} noRowHover>
        <DataTableColumn
          label={formatMessage({
            id: 'components.ecs.profiles.locationMapping.locationAttribute'
          })}
          property="location_attribute"
        ></DataTableColumn>
        <DataTableColumn
          label={formatMessage({
            id: 'components.ecs.profiles.locationMapping.profileAttribute'
          })}
          property="profile_attribute"
        >
          <CustomDataTableCell
            property="profile_attribute"
            attributes={this.props.attributes}
            mapping={this.props.mapping}
            rules={this.props.rules}
            onSelect={changes => {
              this.props.onMappingChange(changes);
            }}
          />
        </DataTableColumn>
      </DataTable>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const mappingTable = this.renderMappingTable();
    const helpLink =
      'https://help.salesforce.com/s/articleView?id=sf.mc_pb_manage_profile_attributes.htm&type=5';

    return (
      <React.Fragment>
        <div className="profile-location-attribute-mapping">
          <div className="location-mapping-checkbox-labels slds-grid slds-grid_vertical">
            <Checkbox
              className="location-mapping-language slds-col"
              labels={{
                label: formatMessage({
                  id:
                    'components.ecs.profiles.locationMapping.languageDescription'
                })
              }}
              value={this.state.showLanguage}
              checked={this.state.showLanguage}
              onChange={(evt, { checked }) => {
                this.setState({ showLanguage: checked });
                if (!checked) {
                  this.props.onMappingChange({
                    language: null
                  });
                }
              }}
            />
            <div className="slds-col">
              <span className="required">*</span>
              <span className="location-required">
                {formatMessage({
                  id: 'components.ecs.profiles.locationMapping.specifyOne'
                })}
              </span>
            </div>
          </div>
          <div className="location-container">
            <Checkbox
              className="location-mapping-checkbox"
              labels={{
                label: formatMessage({
                  id:
                    'components.ecs.profiles.locationMapping.postalCodeCountry'
                })
              }}
              value={this.state.showPostal}
              checked={this.state.showPostal}
              onChange={(evt, { checked }) => {
                this.setState({ showPostal: checked });
                if (!checked) {
                  this.props.onMappingChange({
                    postal_code: null
                  });
                }
              }}
            />
            <Checkbox
              className="location-mapping-checkbox"
              labels={{
                label: formatMessage({
                  id: 'components.ecs.profiles.locationMapping.cityStateCountry'
                })
              }}
              value={this.state.showCity}
              checked={this.state.showCity}
              onChange={(evt, { checked }) => {
                this.setState({ showCity: checked });
                if (!checked) {
                  this.props.onMappingChange({
                    city: null,
                    state: null,
                    country: null
                  });
                }
              }}
            />
            <Checkbox
              className="location-mapping-checkbox"
              labels={{
                label: formatMessage({
                  id: 'components.ecs.profiles.locationMapping.latLong'
                })
              }}
              value={this.state.showLatLong}
              checked={this.state.showLatLong}
              onChange={(evt, { checked }) => {
                this.setState({ showLatLong: checked });
                if (!checked) {
                  this.props.onMappingChange({
                    latitude: null,
                    longitude: null
                  });
                }
              }}
            />
            <div className="postal">
              <span className="location-required">
                {this.state.showPostal ? (
                  <FormattedMessage
                    id="components.ecs.profiles.locationMapping.postalCodeTip"
                    values={{
                      link: (
                        <a
                          rel="noopener noreferrer"
                          target="_blank"
                          href={helpLink}
                        >
                          {this.props.intl.formatMessage({
                            id: 'components.ecs.nocatalog.helpLinkText'
                          })}
                        </a>
                      )
                    }}
                  />
                ) : null}
              </span>
            </div>
            {mappingTable}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

LocationAttributeMapping.propTypes = {
  attributes: PropTypes.array.isRequired,
  mapping: PropTypes.object.isRequired,
  onMappingChange: PropTypes.func.isRequired,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(LocationAttributeMapping);
